import { Container, Element, Label } from '@playcanvas/pcui';

import type { Annotation, AnnotationLayer } from '../annotations';
import type { Bookmark } from '../bookmarks';
import { ElementType } from '../element';
import { Events } from '../events';
import type { Tour, TourStep, TourStepCustomView } from '../tours';
import { localize } from './localization';
import { SceneTree } from './scene-tree';
import addSvg from './svg/add.svg';
import annotationDamageTypeSvg from './svg/annotation-damage-type.svg';
import annotationEvidenceTypeSvg from './svg/annotation-evidence-type.svg';
import annotationPinTypeSvg from './svg/annotation-pin-type.svg';
import annotationPoiTypeSvg from './svg/annotation-poi-type.svg';
import annotationVehicleTypeSvg from './svg/annotation-vehicle-type.svg';
import annotationWitnessTypeSvg from './svg/annotation-witness-type.svg';
import cameraResetSvg from './svg/camera-reset.svg';
import deleteSvg from './svg/delete.svg';
import hiddenSvg from './svg/hidden.svg';
import sceneImportSvg from './svg/import.svg';
import sceneNewSvg from './svg/new.svg';
import sectionAnnotationSvg from './svg/section-annotation.svg';
import sectionBookmarkSvg from './svg/section-bookmark.svg';
import sectionMeasureSvg from './svg/section-measure.svg';
import sectionTourSvg from './svg/section-tour.svg';
import shownSvg from './svg/shown.svg';
import soloSvg from './svg/solo.svg';
import { Tooltips } from './tooltips';
import { Transform } from './transform';

const ANNOTATION_TYPE_SVGS: Record<Annotation['type'], string> = {
    pin: annotationPinTypeSvg,
    evidence: annotationEvidenceTypeSvg,
    witness: annotationWitnessTypeSvg,
    vehicle: annotationVehicleTypeSvg,
    poi: annotationPoiTypeSvg,
    damage: annotationDamageTypeSvg
};

const ANNOTATION_TYPE_COLORS: Record<Annotation['type'], string> = {
    pin: '#4AB0F2',
    evidence: '#FACC3D',
    witness: '#B57DED',
    vehicle: '#30C48C',
    poi: '#ED7840',
    damage: '#ED3D52'
};

const createSvg = (svgString: string) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement;
};

const createHeaderIcon = (svgString: string) => {
    // Wrap SVGs so `.panel-header-icon svg { ... }` applies consistently.
    // (When the root node is an <svg>, nested SVG rules won't match.)
    const wrapper = document.createElement('div');
    wrapper.classList.add('panel-header-icon');
    wrapper.appendChild(createSvg(svgString));
    return new Element({
        dom: wrapper
    });
};

const makeCollapsible = (header: Container, content: Container | Element, startCollapsed = false) => {
    header.class.add('collapsible');

    const chevron = new Label({
        text: '\u25BE',
        class: 'panel-header-collapse'
    });

    // Insert chevron after the icon (first child)
    const children = header.dom.children;
    if (children.length > 1) {
        header.dom.insertBefore(chevron.dom, children[1]);
    } else {
        header.dom.appendChild(chevron.dom);
    }

    if (startCollapsed) {
        header.class.add('collapsed');
        chevron.class.add('collapsed');
        content.hidden = true;
    }

    header.dom.addEventListener('click', (e: Event) => {
        // Don't collapse when clicking header buttons
        const target = e.target as HTMLElement;
        if (target.closest('.panel-header-button')) {
            return;
        }
        const isCollapsed = header.class.contains('collapsed');
        if (isCollapsed) {
            header.class.remove('collapsed');
            chevron.class.remove('collapsed');
            content.hidden = false;
        } else {
            header.class.add('collapsed');
            chevron.class.add('collapsed');
            content.hidden = true;
        }
    });
};

class ScenePanel extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'scene-panel',
            class: 'panel'
        };

        super(args);

        // stop pointer events bubbling
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const sceneHeader = new Container({
            class: 'panel-header'
        });

        const sceneIcon = new Label({
            text: '\uE344',
            class: 'panel-header-icon'
        });

        const sceneLabel = new Label({
            text: localize('panel.scene-manager'),
            class: 'panel-header-label'
        });

        let soloActive = false;

        const soloToggle = new Container({
            class: 'panel-header-button'
        });
        soloToggle.dom.appendChild(createSvg(soloSvg));

        soloToggle.on('click', () => {
            soloActive = !soloActive;
            if (soloActive) {
                soloToggle.class.add('active');
            } else {
                soloToggle.class.remove('active');
            }
            events.fire('scene.solo', soloActive);
        });

        const sceneImport = new Container({
            class: 'panel-header-button'
        });
        sceneImport.dom.appendChild(createSvg(sceneImportSvg));

        const sceneNew = new Container({
            class: 'panel-header-button'
        });
        sceneNew.dom.appendChild(createSvg(sceneNewSvg));

        sceneHeader.append(sceneIcon);
        sceneHeader.append(sceneLabel);
        sceneHeader.append(soloToggle);
        sceneHeader.append(sceneImport);
        sceneHeader.append(sceneNew);

        sceneImport.on('click', async () => {
            await events.invoke('scene.import');
        });

        sceneNew.on('click', () => {
            events.invoke('doc.new');
        });

        tooltips.register(soloToggle, localize('tooltip.scene.solo'), 'top');
        tooltips.register(sceneImport, 'Import Scene', 'top');
        tooltips.register(sceneNew, 'New Scene', 'top');

        // Unified scene hierarchy tree (splats + meshes + helpers)
        const sceneTree = new SceneTree(events);

        // Global clip box row (selectable)
        const clipBoxRow = new Container({
            class: ['splat-item', 'visible']
        });

        const clipBoxText = new Label({
            class: 'splat-item-text',
            text: 'Global Clip Box'
        });

        // Eye icon (wireframe visibility toggle)
        const clipBoxVisible = new Element({
            dom: createSvg(shownSvg),
            class: 'splat-item-visible'
        });

        const clipBoxInvisible = new Element({
            dom: createSvg(hiddenSvg),
            class: 'splat-item-visible',
            hidden: true
        });

        // Reset icon (fit to all content)
        const clipBoxReset = new Element({
            dom: createSvg(cameraResetSvg),
            class: ['splat-item-delete', 'clipbox-reset']
        });

        const clipBoxActions = new Container({
            class: 'clipbox-actions'
        });

        clipBoxActions.append(clipBoxVisible);
        clipBoxActions.append(clipBoxInvisible);
        clipBoxActions.append(clipBoxReset);

        clipBoxRow.append(clipBoxText);
        clipBoxRow.append(clipBoxActions);

        tooltips.register(clipBoxText, 'Select to edit clip box with Move/Rotate/Scale tools. Enable/disable in View Options.', 'left');
        tooltips.register(clipBoxVisible, 'Hide wireframe', 'left');
        tooltips.register(clipBoxInvisible, 'Show wireframe', 'left');
        tooltips.register(clipBoxReset, 'Fit to all content', 'left');

        // Toggle wireframe visibility
        const toggleWireframe = (e: Event) => {
            e.stopPropagation();
            const visible = !clipBoxInvisible.hidden;
            clipBoxVisible.hidden = !visible;
            clipBoxInvisible.hidden = visible;
            if (visible) {
                clipBoxRow.class.add('visible');
            } else {
                clipBoxRow.class.remove('visible');
            }
            events.fire('clipBox.wireframe', visible);
        };

        clipBoxVisible.dom.addEventListener('click', toggleWireframe);
        clipBoxInvisible.dom.addEventListener('click', toggleWireframe);

        // Reset clip box to fit all content
        clipBoxReset.dom.addEventListener('click', (e) => {
            e.stopPropagation();
            events.fire('clipBox.fitAll');
        });

        // Click to select (but don't force enable)
        const selectClipBox = (e: Event) => {
            e.stopPropagation();
            const scn = (window as any).scene;
            const clipBoxEl = scn?.getElementsByType?.(ElementType.clipBox)?.[0];
            if (clipBoxEl) {
                events.fire('selection', clipBoxEl);
            }
        };

        clipBoxText.dom.addEventListener('pointerdown', selectClipBox);
        clipBoxRow.dom.addEventListener('pointerdown', selectClipBox);

        // selection highlight
        events.on('selection.changed', (selection: any) => {
            const isSelected = selection?.type === ElementType.clipBox;
            if (isSelected) {
                clipBoxRow.class.add('selected');
            } else {
                clipBoxRow.class.remove('selected');
            }
        });

        const transformHeader = new Container({
            class: 'panel-header'
        });

        const transformIcon = new Label({
            text: '\uE111',
            class: 'panel-header-icon'
        });

        const transformLabel = new Label({
            text: localize('panel.scene-manager.transform'),
            class: 'panel-header-label'
        });

        transformHeader.append(transformIcon);
        transformHeader.append(transformLabel);

        type BookmarkItem = Bookmark;

        type CameraPose = {
            position?: { x?: number; y?: number; z?: number };
            target?: { x?: number; y?: number; z?: number };
        };

        const toVecTuple = (value: unknown): [number, number, number] | null => {
            if (!value || typeof value !== 'object') {
                return null;
            }
            const candidate = value as Record<string, unknown>;
            const x = candidate.x;
            const y = candidate.y;
            const z = candidate.z;
            if (typeof x !== 'number' || !Number.isFinite(x) || typeof y !== 'number' || !Number.isFinite(y) || typeof z !== 'number' || !Number.isFinite(z)) {
                return null;
            }
            return [x, y, z];
        };

        let bookmarks: BookmarkItem[] = [];
        let selectedBookmarkId: string | null = null;

        const bookmarkRowMap = new Map<string, Container>();

        const setBookmarkSelectedRow = (selectedId: string | null) => {
            bookmarkRowMap.forEach((row, id) => {
                if (id === selectedId) {
                    row.class.add('selected');
                } else {
                    row.class.remove('selected');
                }
            });
        };

        const nextBookmarkName = () => {
            const used = new Set(bookmarks.map(bookmark => bookmark.name));
            let index = 1;
            while (used.has(`Bookmark ${index}`)) {
                index++;
            }
            return `Bookmark ${index}`;
        };

        const buildBookmarkPayload = (): BookmarkItem | null => {
            const pose = events.invoke('camera.getPose') as CameraPose | undefined;

            const position = toVecTuple(pose?.position);
            const target = toVecTuple(pose?.target);
            if (!position || !target) {
                return null;
            }

            const nowIso = new Date().toISOString();
            const id = typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `bookmark-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

            const payload: BookmarkItem = {
                id,
                name: nextBookmarkName(),
                cameraPose: {
                    position,
                    target
                },
                createdAt: nowIso
            };

            const controlMode = events.invoke('camera.controlMode');
            if (controlMode === 'orbit' || controlMode === 'fly') {
                payload.controlMode = controlMode;
            }

            const fov = events.invoke('camera.fov');
            if (typeof fov === 'number' && Number.isFinite(fov)) {
                payload.cameraPose.fov = fov;
            }

            const annotationLayers = events.invoke('annotations.layers') as Array<{ id?: string; visible?: boolean }> | undefined;
            if (Array.isArray(annotationLayers)) {
                const visibleLayerIds = annotationLayers
                .filter(layer => typeof layer?.id === 'string' && !!layer.visible)
                .map(layer => layer.id as string);
                if (visibleLayerIds.length > 0) {
                    payload.visibleLayerIds = visibleLayerIds;
                }
            }

            return payload;
        };

        const buildCustomTourStep = (): TourStepCustomView | null => {
            const pose = events.invoke('camera.getPose') as CameraPose | undefined;
            const position = toVecTuple(pose?.position);
            const target = toVecTuple(pose?.target);
            if (!position || !target) {
                return null;
            }

            const measurements = events.invoke('measurements.list') as Array<{ id?: string; visible?: boolean }> | undefined;
            const measurementVisibleIds = Array.isArray(measurements) ? measurements
            .filter(item => typeof item?.id === 'string' && item.visible !== false)
            .map(item => item.id as string) : undefined;

            const bookmarkItems = events.invoke('bookmarks.list') as Array<{ id?: string; visible?: boolean }> | undefined;
            const bookmarkVisibleIds = Array.isArray(bookmarkItems) ? bookmarkItems
            .filter(item => typeof item?.id === 'string' && item.visible !== false)
            .map(item => item.id as string) : undefined;

            return {
                type: 'customView',
                cameraPose: {
                    position,
                    target
                },
                transitionDuration: 1,
                pauseDuration: 1,
                measurementVisibleIds,
                bookmarkVisibleIds
            };
        };

        const bookmarksHeader = new Container({
            class: 'panel-header'
        });

        const bookmarksIcon = createHeaderIcon(sectionBookmarkSvg);

        const bookmarksLabel = new Label({
            text: 'Bookmarks',
            class: 'panel-header-label'
        });

        const bookmarksSave = new Container({
            id: 'bookmarks-save-button',
            class: 'panel-header-button'
        });
        bookmarksSave.dom.appendChild(createSvg(addSvg));

        bookmarksHeader.append(bookmarksIcon);
        bookmarksHeader.append(bookmarksLabel);
        bookmarksHeader.append(bookmarksSave);

        tooltips.register(bookmarksSave, 'Add Bookmark', 'top');

        const bookmarksList = new Container({
            id: 'bookmarks-list',
            class: 'splat-list'
        });

        const bookmarksListContainer = new Container({
            class: 'bookmarks-list-container'
        });
        bookmarksListContainer.append(bookmarksList);

        const moveBookmark = (id: string, delta: -1 | 1) => {
            const index = bookmarks.findIndex(bookmark => bookmark.id === id);
            if (index < 0) {
                return;
            }
            const nextIndex = index + delta;
            if (nextIndex < 0 || nextIndex >= bookmarks.length) {
                return;
            }

            const orderedIds = bookmarks.map(bookmark => bookmark.id);
            const temp = orderedIds[index];
            orderedIds[index] = orderedIds[nextIndex];
            orderedIds[nextIndex] = temp;
            events.fire('bookmarks.reorder', orderedIds);
        };

        const renderBookmarks = (arr: unknown) => {
            bookmarks = Array.isArray(arr) ? arr.filter((item): item is BookmarkItem => !!item && typeof (item as BookmarkItem).id === 'string' && typeof (item as BookmarkItem).name === 'string') : [];

            bookmarkRowMap.clear();
            bookmarksList.clear();

            if (bookmarks.length === 0) {
                const empty = new Label({
                    class: 'splat-item-text',
                    text: '—'
                });
                const emptyRow = new Container({
                    class: ['splat-item', 'visible']
                });
                emptyRow.append(empty);
                bookmarksList.append(emptyRow);
                return;
            }

            bookmarks.forEach((bookmark, index) => {
                const vis = bookmark.visible !== false;
                const row = new Container({
                    class: ['splat-item', 'visible', 'bookmark-item']
                });
                row.dom.setAttribute('data-bookmark-id', bookmark.id);

                if (!vis) {
                    row.class.remove('visible');
                }

                const text = new Label({
                    class: 'splat-item-text',
                    text: bookmark.name
                });

                const renameInput = document.createElement('input');
                renameInput.type = 'text';
                renameInput.value = bookmark.name;
                renameInput.className = 'bookmark-rename-input';
                renameInput.hidden = true;
                renameInput.setAttribute('data-bookmark-rename-input', bookmark.id);

                let editingName = false;

                const setEditingName = (editing: boolean) => {
                    editingName = editing;
                    text.hidden = editing;
                    renameInput.hidden = !editing;
                    if (editing) {
                        renameInput.value = bookmark.name;
                        queueMicrotask(() => {
                            renameInput.focus();
                            renameInput.select();
                        });
                    }
                };

                const commitRename = () => {
                    const nextName = renameInput.value.trim();
                    setEditingName(false);
                    if (!nextName || nextName === bookmark.name) {
                        return;
                    }
                    events.fire('bookmarks.update', {
                        id: bookmark.id,
                        patch: {
                            name: nextName
                        }
                    });
                };

                const up = new Container({
                    class: ['panel-header-button', 'bookmark-action-button', 'bookmark-action-up']
                });
                up.dom.textContent = '\u2191';
                up.enabled = index > 0;

                const down = new Container({
                    class: ['panel-header-button', 'bookmark-action-button', 'bookmark-action-down']
                });
                down.dom.textContent = '\u2193';
                down.enabled = index < bookmarks.length - 1;

                const remove = new Element({
                    dom: createSvg(deleteSvg),
                    class: ['splat-item-delete', 'bookmark-action-remove']
                });

                const visible = new Element({
                    dom: createSvg(shownSvg),
                    class: 'splat-item-visible'
                });

                const invisible = new Element({
                    dom: createSvg(hiddenSvg),
                    class: 'splat-item-visible',
                    hidden: true
                });

                visible.hidden = !vis;
                invisible.hidden = vis;

                row.append(text);
                row.dom.appendChild(renameInput);
                row.append(visible);
                row.append(invisible);
                row.append(up);
                row.append(down);
                row.append(remove);

                const goTo = (event: Event) => {
                    if (editingName) {
                        return;
                    }
                    event.stopPropagation();
                    events.fire('bookmarks.select', bookmark.id);
                    events.fire('bookmarks.goTo', bookmark.id);
                };

                const moveUp = (event: Event) => {
                    event.stopPropagation();
                    moveBookmark(bookmark.id, -1);
                };

                const moveDown = (event: Event) => {
                    event.stopPropagation();
                    moveBookmark(bookmark.id, 1);
                };

                const removeBookmark = (event: Event) => {
                    event.stopPropagation();
                    events.fire('bookmarks.remove', bookmark.id);
                };

                const toggleVisible = (event: Event) => {
                    event.stopPropagation();
                    const currentlyVisible = row.class.contains('visible');
                    events.fire('bookmarks.setVisible', { id: bookmark.id, visible: !currentlyVisible });
                };

                const toggleVisibleDblClick = (event: MouseEvent) => {
                    event.stopPropagation();
                    const target = event.target as Node | null;
                    if (target) {
                        if (visible.dom.contains(target) || invisible.dom.contains(target) || remove.dom.contains(target) || up.dom.contains(target) || down.dom.contains(target) || text.dom.contains(target) || renameInput.contains(target)) {
                            return;
                        }
                    }
                    const currentlyVisible = row.class.contains('visible');
                    events.fire('bookmarks.setVisible', { id: bookmark.id, visible: !currentlyVisible });
                };

                const startRename = (event: Event) => {
                    event.stopPropagation();
                    setEditingName(true);
                };

                row.dom.addEventListener('click', goTo);
                row.dom.addEventListener('dblclick', toggleVisibleDblClick);
                text.dom.addEventListener('click', goTo);
                text.dom.addEventListener('dblclick', startRename);
                renameInput.addEventListener('pointerdown', event => event.stopPropagation());
                renameInput.addEventListener('click', event => event.stopPropagation());
                renameInput.addEventListener('blur', () => commitRename());
                renameInput.addEventListener('keydown', (event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        commitRename();
                        return;
                    }

                    if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setEditingName(false);
                    }
                });
                up.dom.addEventListener('pointerdown', event => event.stopPropagation());
                down.dom.addEventListener('pointerdown', event => event.stopPropagation());
                remove.dom.addEventListener('pointerdown', event => event.stopPropagation());
                up.dom.addEventListener('click', moveUp);
                down.dom.addEventListener('click', moveDown);
                remove.dom.addEventListener('click', removeBookmark);
                visible.dom.addEventListener('click', toggleVisible);
                invisible.dom.addEventListener('click', toggleVisible);

                bookmarkRowMap.set(bookmark.id, row);
                bookmarksList.append(row);
            });

            // Ensure the visual selection stays in sync after re-render.
            if (events.functions.has('bookmarks.selected')) {
                setBookmarkSelectedRow((events.invoke('bookmarks.selected') as string | null) ?? null);
            }
        };

        bookmarksSave.on('click', () => {
            const payload = buildBookmarkPayload();
            if (!payload) {
                return;
            }
            events.fire('bookmarks.add', payload);
        });

        events.on('bookmarks.listChanged', (arr: unknown) => {
            renderBookmarks(arr);
        });

        // Tours section select element.
        // Create early so bookmark selection changes can keep it in sync without worrying about init ordering.
        const addBookmarkSelect = document.createElement('select');

        events.on('bookmarks.selectedChanged', (selectedId: string | null) => {
            selectedBookmarkId = selectedId;
            setBookmarkSelectedRow(selectedId);
            // keep Tours dropdown in sync with the selected bookmark
            if (selectedId && Array.isArray(bookmarks) && bookmarks.some(b => b.id === selectedId)) {
                addBookmarkSelect.value = selectedId;
            } else {
                addBookmarkSelect.value = '';
            }
        });

        renderBookmarks(events.invoke('bookmarks.list'));

        // ---- Annotations section ----

        const annotationsHeader = new Container({
            class: 'panel-header'
        });

        const annotationsIcon = createHeaderIcon(sectionAnnotationSvg);

        const annotationsLabel = new Label({
            text: 'Annotations',
            class: 'panel-header-label'
        });

        const annotationsAdd = new Container({
            id: 'annotations-add-button',
            class: 'panel-header-button'
        });
        annotationsAdd.dom.appendChild(createSvg(addSvg));

        annotationsHeader.append(annotationsIcon);
        annotationsHeader.append(annotationsLabel);
        annotationsHeader.append(annotationsAdd);

        annotationsAdd.on('click', () => {
            events.fire('tool.annotate');
        });

        tooltips.register(annotationsAdd, 'Place Annotation', 'top');

        const annotationsList = new Container({
            class: 'splat-list'
        });

        const annotationsListContainer = new Container({
            class: 'annotations-list-container'
        });
        annotationsListContainer.append(annotationsList);

        const focusAnnotation = (annotation: Annotation) => {
            const pose = (events.invoke('camera.getPose') as CameraPose | undefined) ?? {};
            const position = pose.position;
            const target = pose.target;

            const pointX = annotation.worldPosition[0];
            const pointY = annotation.worldPosition[1];
            const pointZ = annotation.worldPosition[2];

            const dirX = (target?.x ?? pointX) - (position?.x ?? pointX);
            const dirY = (target?.y ?? pointY) - (position?.y ?? pointY);
            const dirZ = (target?.z ?? (pointZ - 1)) - (position?.z ?? pointZ);
            const dirLen = Math.hypot(dirX, dirY, dirZ);

            const normX = dirLen > 1e-5 ? dirX / dirLen : 0;
            const normY = dirLen > 1e-5 ? dirY / dirLen : 0;
            const normZ = dirLen > 1e-5 ? dirZ / dirLen : -1;

            const currentDistance = dirLen > 0 ? dirLen : 4;
            const focusDistance = Math.max(1.5, Math.min(8, currentDistance * 0.35));

            events.fire('camera.setPose', {
                position: {
                    x: pointX - normX * focusDistance,
                    y: pointY - normY * focusDistance,
                    z: pointZ - normZ * focusDistance
                },
                target: {
                    x: pointX,
                    y: pointY,
                    z: pointZ
                }
            }, 1);
        };

        const annotationRowMap = new Map<string, Container>();

        const setAnnotationSelectedRow = (selectedId: string | null) => {
            annotationRowMap.forEach((row, id) => {
                if (id === selectedId) {
                    row.class.add('selected');
                } else {
                    row.class.remove('selected');
                }
            });
        };

        const renderAnnotations = (arr: unknown) => {
            const items: Annotation[] = Array.isArray(arr) ? arr.filter((item): item is Annotation => !!item && typeof (item as Annotation).id === 'string') : [];

            annotationRowMap.clear();
            annotationsList.clear();

            if (items.length === 0) {
                const empty = new Label({
                    class: 'splat-item-text',
                    text: '—'
                });
                const emptyRow = new Container({
                    class: ['splat-item', 'visible']
                });
                emptyRow.append(empty);
                annotationsList.append(emptyRow);
                return;
            }

            items.forEach((annotation) => {
                const row = new Container({
                    class: ['splat-item', 'visible', 'annotation-item']
                });
                row.dom.setAttribute('data-annotation-id', annotation.id);

                // Colored type SVG icon
                const svgStr = ANNOTATION_TYPE_SVGS[annotation.type] ?? ANNOTATION_TYPE_SVGS.pin;
                const iconEl = createSvg(svgStr);
                iconEl.setAttribute('width', '16');
                iconEl.setAttribute('height', '16');
                iconEl.style.color = ANNOTATION_TYPE_COLORS[annotation.type] ?? ANNOTATION_TYPE_COLORS.pin;
                const icon = new Element({
                    dom: iconEl,
                    class: 'annotation-item-icon'
                });

                const text = new Label({
                    class: 'splat-item-text',
                    text: annotation.label || `${annotation.type} annotation`
                });

                // Layer badge (if annotation belongs to a named layer)
                if (annotation.layerId) {
                    const allLayers = (events.invoke('annotations.layers') as AnnotationLayer[]) ?? [];
                    const layer = allLayers.find(l => l.id === annotation.layerId);
                    if (layer) {
                        const badge = new Label({
                            class: 'annotation-layer-badge',
                            text: layer.name
                        });
                        text.dom.after(badge.dom);
                    }
                }

                const remove = new Element({
                    dom: createSvg(deleteSvg),
                    class: 'splat-item-delete'
                });

                row.append(icon);
                row.append(text);
                row.append(remove);

                // Hover events for cross-highlighting
                row.dom.addEventListener('pointerenter', () => {
                    events.fire('annotations.hover', annotation.id);
                });
                row.dom.addEventListener('pointerleave', () => {
                    events.fire('annotations.hover', null);
                });

                // Click row → select + focus camera
                row.dom.addEventListener('pointerdown', (e: Event) => {
                    e.stopPropagation();
                    events.fire('annotations.select', annotation.id);
                    focusAnnotation(annotation);
                });

                // Double-click label → open annotation panel for full editing
                text.dom.addEventListener('dblclick', (e: Event) => {
                    e.stopPropagation();
                    events.fire('annotations.select', annotation.id);
                    events.fire('annotationPanel.setVisible', true);
                });

                // Delete button
                remove.dom.addEventListener('pointerdown', (e: Event) => e.stopPropagation());
                remove.dom.addEventListener('click', (e: Event) => {
                    e.stopPropagation();
                    events.fire('annotations.remove', annotation.id);
                });

                annotationRowMap.set(annotation.id, row);
                annotationsList.append(row);
            });
        };

        events.on('annotations.listChanged', (arr: unknown) => {
            renderAnnotations(arr);
        });

        events.on('annotations.selectedChanged', (selectedId: string | null) => {
            setAnnotationSelectedRow(selectedId);
        });

        renderAnnotations(events.invoke('annotations.list'));

        // ---- End Annotations section ----

        const toursHeader = new Container({
            class: 'panel-header'
        });

        const toursIcon = createHeaderIcon(sectionTourSvg);

        const toursLabel = new Label({
            text: 'Tours',
            class: 'panel-header-label'
        });

        const toursNew = new Container({
            id: 'tours-new-button',
            class: 'panel-header-button'
        });
        toursNew.dom.appendChild(createSvg(addSvg));

        toursHeader.append(toursIcon);
        toursHeader.append(toursLabel);
        toursHeader.append(toursNew);

        tooltips.register(toursNew, 'New Tour', 'top');

        const toursContainer = new Container({
            class: 'tours-panel-container'
        });

        const toursControls = document.createElement('div');
        toursControls.className = 'tours-controls';

        const toursSelect = document.createElement('select');
        toursSelect.id = 'tours-active-select';
        toursSelect.className = 'tours-select';

        const toursPlay = document.createElement('button');
        toursPlay.type = 'button';
        toursPlay.id = 'tours-play-button';
        toursPlay.className = 'tours-button';
        toursPlay.textContent = 'Play Tour';
        toursPlay.disabled = true;
        toursPlay.title = 'Tour playback is enabled in P3.2';

        const toursRename = document.createElement('button');
        toursRename.type = 'button';
        toursRename.className = 'tours-button';
        toursRename.textContent = 'Rename';
        toursRename.title = 'Rename selected tour';
        toursRename.disabled = true;

        const toursDelete = document.createElement('button');
        toursDelete.type = 'button';
        toursDelete.className = 'tours-button';
        toursDelete.textContent = 'Delete';
        toursDelete.title = 'Delete selected tour';
        toursDelete.disabled = true;

        toursControls.appendChild(toursSelect);
        toursControls.appendChild(toursPlay);
        toursControls.appendChild(toursRename);
        toursControls.appendChild(toursDelete);

        const tourStepActions = document.createElement('div');
        tourStepActions.className = 'tours-step-actions';

        addBookmarkSelect.id = 'tour-step-bookmark-select';
        addBookmarkSelect.className = 'tours-select';

        const addBookmarkStepButton = document.createElement('button');
        addBookmarkStepButton.type = 'button';
        addBookmarkStepButton.id = 'tour-add-bookmark-step';
        addBookmarkStepButton.className = 'tours-button';
        addBookmarkStepButton.textContent = 'Add Bookmark Step';

        const addCurrentViewStepButton = document.createElement('button');
        addCurrentViewStepButton.type = 'button';
        addCurrentViewStepButton.id = 'tour-add-current-view-step';
        addCurrentViewStepButton.className = 'tours-button';
        addCurrentViewStepButton.textContent = 'Add Current View Step';

        tourStepActions.appendChild(addBookmarkSelect);
        tourStepActions.appendChild(addBookmarkStepButton);
        tourStepActions.appendChild(addCurrentViewStepButton);

        const toursMessage = document.createElement('div');
        toursMessage.id = 'tours-panel-message';
        toursMessage.className = 'tours-message';

        const toursList = document.createElement('div');
        toursList.id = 'tour-steps-list';
        toursList.className = 'tours-steps-list';

        toursContainer.dom.appendChild(toursControls);
        toursContainer.dom.appendChild(tourStepActions);
        toursContainer.dom.appendChild(toursMessage);
        toursContainer.dom.appendChild(toursList);

        let tours = (events.invoke('tours.list') as Tour[]) ?? [];
        let activeTourId = tours[0]?.id ?? '';

        const getBookmarkName = (bookmarkId: string) => {
            const bookmark = bookmarks.find(item => item.id === bookmarkId);
            return bookmark?.name ?? 'Unknown bookmark';
        };

        const getActiveTour = () => {
            return tours.find(tour => tour.id === activeTourId) ?? null;
        };

        const isTourPlaybackEnabled = () => {
            return events.functions.has('tours.playback.enabled') && events.invoke('tours.playback.enabled') === true;
        };

        const setTourMessage = (text: string, isError = false) => {
            toursMessage.textContent = text;
            if (isError) {
                toursMessage.classList.add('tours-message-error');
            } else {
                toursMessage.classList.remove('tours-message-error');
            }
        };

        const updateStepTiming = (tourId: string, stepIndex: number, key: 'transitionDuration' | 'pauseDuration', rawValue: string) => {
            const activeTour = tours.find(item => item.id === tourId);
            if (!activeTour) {
                return;
            }

            const value = Number(rawValue);
            if (!Number.isFinite(value) || value < 0) {
                setTourMessage('Timing values must be non-negative numbers.', true);
                return;
            }

            const nextSteps = activeTour.steps.map((step, index) => {
                if (index !== stepIndex) {
                    return step;
                }
                return {
                    ...step,
                    [key]: value
                };
            });

            events.fire('tours.update', {
                id: tourId,
                patch: {
                    steps: nextSteps
                }
            });
            setTourMessage('');
        };

        const moveTourStep = (tourId: string, fromIndex: number, delta: -1 | 1) => {
            const tour = tours.find(item => item.id === tourId);
            if (!tour) {
                return;
            }

            const toIndex = fromIndex + delta;
            if (fromIndex < 0 || fromIndex >= tour.steps.length || toIndex < 0 || toIndex >= tour.steps.length) {
                return;
            }

            const indices = Array.from({ length: tour.steps.length }, (_, index) => index);
            const temp = indices[fromIndex];
            indices[fromIndex] = indices[toIndex];
            indices[toIndex] = temp;
            events.fire('tours.reorderSteps', tourId, indices);
        };

        const previewTourStep = (step: TourStep) => {
            if (step.layerStateOverride) {
                for (const [layerId, visible] of Object.entries(step.layerStateOverride)) {
                    events.fire('annotations.layers.setVisible', layerId, visible);
                }
            }

            if (Array.isArray(step.measurementVisibleIds)) {
                events.fire('measurements.applyVisibility', step.measurementVisibleIds);
            }
            if (Array.isArray(step.bookmarkVisibleIds)) {
                events.fire('bookmarks.applyVisibility', step.bookmarkVisibleIds);
            }

            if (step.type === 'bookmark') {
                events.invoke('bookmarks.applyView', step.bookmarkId, Math.max(0, step.transitionDuration));
                return;
            }

            events.fire('camera.setPose', {
                position: {
                    x: step.cameraPose.position[0],
                    y: step.cameraPose.position[1],
                    z: step.cameraPose.position[2]
                },
                target: {
                    x: step.cameraPose.target[0],
                    y: step.cameraPose.target[1],
                    z: step.cameraPose.target[2]
                }
            }, step.transitionDuration);

            if (typeof step.cameraPose.fov === 'number' && Number.isFinite(step.cameraPose.fov)) {
                events.fire('camera.setFov', step.cameraPose.fov);
            }
        };

        const renderTourSteps = () => {
            while (toursList.firstChild) {
                toursList.removeChild(toursList.firstChild);
            }

            const activeTour = getActiveTour();
            if (!activeTour) {
                const empty = document.createElement('div');
                empty.className = 'tours-empty-message';
                empty.textContent = 'Select or create a tour.';
                toursList.appendChild(empty);
                return;
            }

            if (activeTour.steps.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'tours-empty-message';
                empty.textContent = 'No steps yet.';
                toursList.appendChild(empty);
                return;
            }

            activeTour.steps.forEach((step, index) => {
                const row = document.createElement('div');
                row.className = 'tours-step-row';
                row.dataset.tourStepIndex = `${index}`;

                const stepLabel = document.createElement('div');
                stepLabel.className = 'tours-step-label';
                const bookmarkLabel = step.type === 'bookmark' ? getBookmarkName(step.bookmarkId) : 'Custom view';
                stepLabel.textContent = `${index + 1}. ${bookmarkLabel}`;

                const transitionInput = document.createElement('input');
                transitionInput.type = 'number';
                transitionInput.step = '0.1';
                transitionInput.min = '0';
                transitionInput.className = 'tours-timing-input';
                transitionInput.value = `${step.transitionDuration}`;
                transitionInput.dataset.tourTransition = `${index}`;
                transitionInput.title = 'Transition duration (seconds)';

                const pauseInput = document.createElement('input');
                pauseInput.type = 'number';
                pauseInput.step = '0.1';
                pauseInput.min = '0';
                pauseInput.className = 'tours-timing-input';
                pauseInput.value = `${step.pauseDuration}`;
                pauseInput.dataset.tourPause = `${index}`;
                pauseInput.title = 'Pause duration (seconds)';

                const previewButton = document.createElement('button');
                previewButton.type = 'button';
                previewButton.className = 'tours-button';
                previewButton.textContent = 'Preview';
                previewButton.dataset.tourPreview = `${index}`;

                const upButton = document.createElement('button');
                upButton.type = 'button';
                upButton.className = 'tours-button';
                upButton.textContent = '\u2191';
                upButton.dataset.tourStepUp = `${index}`;
                upButton.disabled = index === 0;

                const downButton = document.createElement('button');
                downButton.type = 'button';
                downButton.className = 'tours-button';
                downButton.textContent = '\u2193';
                downButton.dataset.tourStepDown = `${index}`;
                downButton.disabled = index === activeTour.steps.length - 1;

                const removeButton = document.createElement('button');
                removeButton.type = 'button';
                removeButton.className = 'tours-button';
                removeButton.textContent = 'Remove';
                removeButton.dataset.tourStepRemove = `${index}`;

                const actionWrap = document.createElement('div');
                actionWrap.className = 'tours-step-actions-wrap';
                actionWrap.appendChild(upButton);
                actionWrap.appendChild(downButton);
                actionWrap.appendChild(removeButton);

                row.appendChild(stepLabel);
                row.appendChild(transitionInput);
                row.appendChild(pauseInput);
                row.appendChild(previewButton);
                row.appendChild(actionWrap);

                transitionInput.addEventListener('change', () => {
                    updateStepTiming(activeTour.id, index, 'transitionDuration', transitionInput.value);
                });

                pauseInput.addEventListener('change', () => {
                    updateStepTiming(activeTour.id, index, 'pauseDuration', pauseInput.value);
                });

                previewButton.addEventListener('click', () => {
                    previewTourStep(step);
                });

                upButton.addEventListener('click', () => {
                    moveTourStep(activeTour.id, index, -1);
                });

                downButton.addEventListener('click', () => {
                    moveTourStep(activeTour.id, index, 1);
                });

                removeButton.addEventListener('click', () => {
                    events.fire('tours.removeStep', activeTour.id, index);
                });

                toursList.appendChild(row);
            });
        };

        const renderActiveTourOptions = () => {
            while (toursSelect.firstChild) {
                toursSelect.removeChild(toursSelect.firstChild);
            }

            if (tours.length === 0) {
                const option = document.createElement('option');
                option.value = '';
                option.textContent = 'No tours';
                toursSelect.appendChild(option);
                toursSelect.value = '';
                return;
            }

            if (!tours.some(tour => tour.id === activeTourId)) {
                activeTourId = tours[0].id;
            }

            tours.forEach((tour) => {
                const option = document.createElement('option');
                option.value = tour.id;
                option.textContent = tour.name;
                toursSelect.appendChild(option);
            });
            toursSelect.value = activeTourId;
        };

        const renderBookmarkStepOptions = () => {
            while (addBookmarkSelect.firstChild) {
                addBookmarkSelect.removeChild(addBookmarkSelect.firstChild);
            }

            const placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.textContent = 'Select bookmark';
            addBookmarkSelect.appendChild(placeholder);

            bookmarks.forEach((bookmark) => {
                const option = document.createElement('option');
                option.value = bookmark.id;
                option.textContent = bookmark.name;
                addBookmarkSelect.appendChild(option);
            });

            // If a bookmark is selected in the list, reflect it here.
            if (selectedBookmarkId && bookmarks.some(b => b.id === selectedBookmarkId)) {
                addBookmarkSelect.value = selectedBookmarkId;
            } else {
                addBookmarkSelect.value = '';
            }
        };

        const renderTours = () => {
            renderActiveTourOptions();
            renderBookmarkStepOptions();
            renderTourSteps();

            const activeTour = getActiveTour();
            const disabled = !activeTour;
            addBookmarkSelect.disabled = disabled || bookmarks.length === 0;
            addBookmarkStepButton.disabled = disabled || bookmarks.length === 0;
            addCurrentViewStepButton.disabled = disabled;
            toursPlay.disabled = disabled || !isTourPlaybackEnabled();
            toursRename.disabled = disabled;
            toursDelete.disabled = disabled;
        };

        toursSelect.addEventListener('change', () => {
            activeTourId = toursSelect.value;
            renderTours();
            setTourMessage('');
        });

        toursNew.on('click', () => {
            const inputName = window.prompt('Tour name'); // eslint-disable-line no-alert
            if (inputName === null) {
                return;
            }

            const name = inputName.trim();
            if (!name) {
                setTourMessage('Tour name is required.', true);
                return;
            }

            const id = typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `tour-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            events.fire('tours.add', {
                id,
                name,
                steps: [],
                createdAt: new Date().toISOString()
            });
            activeTourId = id;
            setTourMessage('');
        });

        toursRename.addEventListener('click', () => {
            const activeTour = getActiveTour();
            if (!activeTour) {
                return;
            }
            const inputName = window.prompt('Rename tour', activeTour.name); // eslint-disable-line no-alert
            if (inputName === null) {
                return;
            }
            const name = inputName.trim();
            if (!name) {
                setTourMessage('Tour name is required.', true);
                return;
            }
            events.fire('tours.update', { id: activeTour.id, patch: { name } });
            setTourMessage('');
        });

        toursDelete.addEventListener('click', () => {
            const activeTour = getActiveTour();
            if (!activeTour) {
                return;
            }
            if (!window.confirm(`Delete tour "${activeTour.name}"?`)) { // eslint-disable-line no-alert
                return;
            }
            const deletedId = activeTour.id;
            events.fire('tours.remove', deletedId);
            const remaining = tours.filter(t => t.id !== deletedId);
            activeTourId = remaining.length > 0 ? remaining[0].id : '';
            setTourMessage('');
        });

        addBookmarkStepButton.addEventListener('click', () => {
            const activeTour = getActiveTour();
            if (!activeTour) {
                setTourMessage('Select a tour first.', true);
                return;
            }

            const bookmarkId = addBookmarkSelect.value;
            if (!bookmarkId || !bookmarks.some(item => item.id === bookmarkId)) {
                setTourMessage('Select a bookmark to add a step.', true);
                return;
            }

            const measurements = events.invoke('measurements.list') as Array<{ id?: string; visible?: boolean }> | undefined;
            const measurementVisibleIds = Array.isArray(measurements) ? measurements
            .filter(item => typeof item?.id === 'string' && item.visible !== false)
            .map(item => item.id as string) : undefined;

            const bookmarkItems = events.invoke('bookmarks.list') as Array<{ id?: string; visible?: boolean }> | undefined;
            const bookmarkVisibleIds = Array.isArray(bookmarkItems) ? bookmarkItems
            .filter(item => typeof item?.id === 'string' && item.visible !== false)
            .map(item => item.id as string) : undefined;

            events.fire('tours.addStep', activeTour.id, {
                type: 'bookmark',
                bookmarkId,
                transitionDuration: 1,
                pauseDuration: 1,
                measurementVisibleIds,
                bookmarkVisibleIds
            });
            addBookmarkSelect.value = '';
            setTourMessage('');
        });

        addCurrentViewStepButton.addEventListener('click', () => {
            const activeTour = getActiveTour();
            if (!activeTour) {
                setTourMessage('Select a tour first.', true);
                return;
            }

            const step = buildCustomTourStep();
            if (!step) {
                setTourMessage('Could not capture current view.', true);
                return;
            }

            events.fire('tours.addStep', activeTour.id, step);
            setTourMessage('');
        });

        toursPlay.addEventListener('click', () => {
            if (!isTourPlaybackEnabled()) {
                return;
            }
            const activeTour = getActiveTour();
            if (!activeTour) {
                return;
            }
            events.fire('tour.play', activeTour.id);
        });

        events.on('tours.listChanged', (arr: unknown) => {
            tours = Array.isArray(arr) ? arr.filter((item): item is Tour => !!item && typeof (item as Tour).id === 'string' && typeof (item as Tour).name === 'string' && Array.isArray((item as Tour).steps)) : [];
            renderTours();
        });

        events.on('bookmarks.listChanged', () => {
            renderTours();
        });

        // Measurements (persistent list)

        const measurementsHeader = new Container({
            class: 'panel-header'
        });

        const measurementsIcon = createHeaderIcon(sectionMeasureSvg);

        const measurementsLabel = new Label({
            text: 'Measurements',
            class: 'panel-header-label'
        });

        const measureUnitsSelect = document.createElement('select');
        measureUnitsSelect.className = 'measurement-units-select';
        measureUnitsSelect.title = 'Measurement units';
        (['m', 'ft'] as const).forEach((unit) => {
            const opt = document.createElement('option');
            opt.value = unit;
            opt.textContent = unit;
            measureUnitsSelect.appendChild(opt);
        });

        const measurementsExportCsv = new Container({
            class: ['panel-header-button', 'measurement-export-csv']
        });
        measurementsExportCsv.dom.textContent = 'CSV';

        const measurementsClear = new Container({
            class: 'panel-header-button'
        });
        measurementsClear.dom.appendChild(createSvg(deleteSvg));

        measurementsHeader.append(measurementsIcon);
        measurementsHeader.append(measurementsLabel);
        measurementsHeader.dom.appendChild(measureUnitsSelect);
        measurementsHeader.append(measurementsExportCsv);
        measurementsHeader.append(measurementsClear);

        tooltips.register(measurementsExportCsv, 'Export measurements as CSV', 'top');
        tooltips.register(measurementsClear, 'Clear all measurements', 'top');

        const measurementsList = new Container({
            class: ['splat-list', 'measurement-table-list']
        });

        const measurementsListContainer = new Container({
            class: 'measurements-list-container'
        });
        measurementsListContainer.append(measurementsList);

        const measurementItems = new Map<number, Container>();

        type MeasurementRow = {
            id?: string;
            number?: number;
            name?: string;
            description?: string;
            kind?: 'distance' | 'polyline' | 'area' | 'angle';
            length?: number;
            area?: number;
            units?: 'm' | 'ft';
            visible?: boolean;
            points?: [number, number, number][];
        };

        const formatValue = (measurement: MeasurementRow) => {
            if (measurement.kind === 'area') {
                const value = Number(measurement.area);
                return Number.isFinite(value) ? value.toFixed(2) : '0.00';
            }

            if (measurement.kind === 'angle') {
                const points = Array.isArray(measurement.points) ? measurement.points : [];
                if (points.length !== 3) {
                    return '0.00';
                }
                const [a, b, c] = points;
                const abx = a[0] - b[0];
                const aby = a[1] - b[1];
                const abz = a[2] - b[2];
                const cbx = c[0] - b[0];
                const cby = c[1] - b[1];
                const cbz = c[2] - b[2];
                const abLen = Math.sqrt(abx * abx + aby * aby + abz * abz);
                const cbLen = Math.sqrt(cbx * cbx + cby * cby + cbz * cbz);
                if (abLen <= 1e-8 || cbLen <= 1e-8) {
                    return '0.00';
                }
                const dot = abx * cbx + aby * cby + abz * cbz;
                const cosTheta = Math.max(-1, Math.min(1, dot / (abLen * cbLen)));
                return (Math.acos(cosTheta) * (180 / Math.PI)).toFixed(2);
            }

            const value = Number(measurement.length);
            return Number.isFinite(value) ? value.toFixed(2) : '0.00';
        };

        const formatUnits = (measurement: MeasurementRow) => {
            if (measurement.kind === 'angle') {
                return 'deg';
            }
            if (measurement.kind === 'area') {
                return measurement.units === 'ft' ? 'sq ft' : 'sq m';
            }
            return measurement.units === 'ft' ? 'ft' : 'm';
        };

        const makeHeaderRow = () => {
            const header = new Container({
                class: ['splat-item', 'measurement-table-header']
            });

            const idCol = new Label({ class: 'measurement-col-id', text: 'ID/Name' });
            const typeCol = new Label({ class: 'measurement-col-type', text: 'Type' });
            const valueCol = new Label({ class: 'measurement-col-value', text: 'Value' });
            const unitsCol = new Label({ class: 'measurement-col-units', text: 'Units' });
            const descCol = new Label({ class: 'measurement-col-description', text: 'Description' });

            header.append(idCol);
            header.append(typeCol);
            header.append(valueCol);
            header.append(unitsCol);
            header.append(descCol);

            return header;
        };

        const setSelectedRow = (selected: number | null) => {
            measurementItems.forEach((row, number) => {
                if (number === selected) {
                    row.class.add('selected');
                } else {
                    row.class.remove('selected');
                }
            });
        };

        const renderMeasurements = (arr: any[]) => {
            measurementItems.clear();
            measurementsList.clear();

            measurementsList.append(makeHeaderRow());

            if (!Array.isArray(arr) || arr.length === 0) {
                const empty = new Label({
                    class: 'measurement-col-id',
                    text: '—'
                });
                const emptyRow = new Container({
                    class: ['splat-item', 'measurement-row', 'visible']
                });
                emptyRow.append(empty);
                measurementsList.append(emptyRow);
                return;
            }

            for (const m of arr.slice(0, 50) as MeasurementRow[]) {
                const num = Number(m?.number);
                const vis = !!m?.visible;
                if (!Number.isFinite(num)) {
                    continue;
                }

                const row = new Container({
                    class: ['splat-item', 'measurement-row', 'visible']
                });

                if (!vis) {
                    row.class.remove('visible');
                }

                const idCol = new Container({
                    class: 'measurement-col-id'
                });

                const idLabel = new Label({
                    class: 'measurement-id-label',
                    text: `#${Math.floor(num)}`
                });

                const nameInput = document.createElement('input');
                nameInput.className = 'measurement-inline-input';
                nameInput.placeholder = 'Name';
                nameInput.value = `${m?.name ?? ''}`;

                nameInput.addEventListener('pointerdown', e => e.stopPropagation());
                nameInput.addEventListener('dblclick', e => e.stopPropagation());
                nameInput.addEventListener('change', (e) => {
                    e.stopPropagation();
                    const id = typeof m?.id === 'string' ? m.id : null;
                    if (!id) {
                        return;
                    }
                    events.fire('measurements.update', {
                        id,
                        patch: {
                            name: nameInput.value.trim()
                        }
                    });
                });

                idCol.append(idLabel);
                idCol.dom.appendChild(nameInput);

                const typeCol = new Label({
                    class: 'measurement-col-type',
                    text: `${m?.kind ?? 'distance'}`
                });

                const valueCol = new Label({
                    class: 'measurement-col-value',
                    text: formatValue(m)
                });

                const unitsCol = new Label({
                    class: 'measurement-col-units',
                    text: formatUnits(m)
                });

                const descriptionCol = new Container({
                    class: 'measurement-col-description'
                });

                const descriptionInput = document.createElement('input');
                descriptionInput.className = 'measurement-inline-input';
                descriptionInput.placeholder = 'Description';
                descriptionInput.value = `${m?.description ?? ''}`;

                descriptionInput.addEventListener('pointerdown', e => e.stopPropagation());
                descriptionInput.addEventListener('dblclick', e => e.stopPropagation());
                descriptionInput.addEventListener('change', (e) => {
                    e.stopPropagation();
                    const id = typeof m?.id === 'string' ? m.id : null;
                    if (!id) {
                        return;
                    }
                    events.fire('measurements.update', {
                        id,
                        patch: {
                            description: descriptionInput.value.trim()
                        }
                    });
                });

                descriptionCol.dom.appendChild(descriptionInput);

                const visible = new Element({
                    dom: createSvg(shownSvg),
                    class: 'splat-item-visible'
                });

                const invisible = new Element({
                    dom: createSvg(hiddenSvg),
                    class: 'splat-item-visible',
                    hidden: true
                });

                visible.hidden = !vis;
                invisible.hidden = vis;

                const remove = new Element({
                    dom: createSvg(deleteSvg),
                    class: 'splat-item-delete'
                });

                row.append(idCol);
                row.append(typeCol);
                row.append(valueCol);
                row.append(unitsCol);
                row.append(descriptionCol);
                row.append(visible);
                row.append(invisible);
                row.append(remove);

                const select = (e: Event) => {
                    e.stopPropagation();
                    events.fire('measurements.select', num);
                };

                const del = (e: Event) => {
                    e.stopPropagation();
                    if (typeof m?.id === 'string') {
                        events.fire('measurements.remove', m.id);
                    } else {
                        events.fire('measurements.delete', num);
                    }
                };

                const toggleVisible = (e: Event) => {
                    e.stopPropagation();
                    const currentlyVisible = row.class.contains('visible');
                    events.fire('measurements.setVisible', { number: num, visible: !currentlyVisible });
                };

                const focusMeasurement = (e: MouseEvent) => {
                    e.stopPropagation();
                    const target = e.target as Node | null;
                    if (target) {
                        if (visible.dom.contains(target) || invisible.dom.contains(target) || remove.dom.contains(target)) {
                            return;
                        }
                    }
                    events.fire('measurements.select', num);
                    events.fire('measurements.focus', typeof m?.id === 'string' ? m.id : num);
                };

                row.dom.addEventListener('pointerdown', select);
                row.dom.addEventListener('dblclick', focusMeasurement);
                visible.dom.addEventListener('pointerdown', e => e.stopPropagation());
                invisible.dom.addEventListener('pointerdown', e => e.stopPropagation());
                remove.dom.addEventListener('pointerdown', e => e.stopPropagation());
                visible.dom.addEventListener('click', toggleVisible);
                invisible.dom.addEventListener('click', toggleVisible);
                remove.dom.addEventListener('click', del);

                measurementItems.set(Math.floor(num), row);
                measurementsList.append(row);
            }
        };

        events.on('measurements.listChanged', (arr: any[]) => {
            renderMeasurements(arr);
        });

        events.on('measurements.selectedChanged', (selected: number | null) => {
            setSelectedRow(selected);
        });

        measurementsExportCsv.on('click', () => {
            const csv = events.invoke('measurements.exportCsv') as string | undefined;
            if (typeof csv !== 'string' || csv.length === 0) {
                return;
            }

            const filename = events.invoke('measurements.csvFilename') as string | undefined;
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = typeof filename === 'string' && filename.length > 0 ? filename : 'measurements.csv';
            anchor.style.display = 'none';
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
        });

        measurementsClear.on('click', async () => {
            const result = await events.invoke('showPopup', {
                type: 'yesno',
                header: 'Clear Measurements',
                message: 'Clear all measurements? This operation can not be undone.'
            });
            if (result?.action === 'yes') {
                events.fire('measurements.clearAll');
            }
        });

        measureUnitsSelect.addEventListener('change', () => {
            events.fire('measure.setUnits', measureUnitsSelect.value);
        });

        events.on('measure.units', (units: string) => {
            measureUnitsSelect.value = units;
        });

        // Collapsible content wrappers
        const transformContent = new Transform(events);
        makeCollapsible(transformHeader, transformContent, true);
        makeCollapsible(measurementsHeader, measurementsListContainer, true);
        makeCollapsible(bookmarksHeader, bookmarksListContainer, true);
        makeCollapsible(annotationsHeader, annotationsListContainer, true);
        makeCollapsible(toursHeader, toursContainer, true);

        // Wrap Scene Manager content in a collapsible container
        const sceneContent = new Container({ class: 'scene-content' });
        sceneContent.append(clipBoxRow);
        sceneContent.append(sceneTree);
        makeCollapsible(sceneHeader, sceneContent);

        // Layout order: Scene Manager, Transform, Measurements, Bookmarks, Tours
        this.append(sceneHeader);
        this.append(sceneContent);
        this.append(transformHeader);
        this.append(transformContent);
        this.append(measurementsHeader);
        this.append(measurementsListContainer);
        this.append(bookmarksHeader);
        this.append(bookmarksListContainer);
        this.append(annotationsHeader);
        this.append(annotationsListContainer);
        this.append(toursHeader);
        this.append(toursContainer);
        this.append(new Element({
            class: 'panel-header',
            height: 20
        }));

        renderTours();
    }
}

export { ScenePanel };
